<script lang="ts">
  import PageMeta from '$lib/components/PageMeta.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import Star from '$lib/components/Star.svelte';
  import ContactCtaSection from '$lib/sections/ContactCtaSection.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';

  const dayOne = [
    'Give your team AI assistants with access to your documents and internal knowledge',
    'Deploy pre-built agents for common workflows — support triage, document processing, data analysis',
    'Connect to your existing data sources securely',
    'Show IT the admin console, the audit trail, and the permission model'
  ];

  const dayThirty = [
    'Custom agents shaped to your specific business processes',
    'Embedded AI on your customer-facing site or internal portal',
    'Teams and Slack integration so AI meets people in the tools they already use',
    'Usage analytics to quantify ROI'
  ];
</script>

<PageMeta
  title="Ops & Product Teams — Ship AI shaped to your workflows"
  description="Deploy AI for your team without a six-month procurement cycle. Working agents in 30 minutes, custom workflows by day 30."
/>

<!-- Top stars -->
<div class="relative">
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-px" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-px" />
</div>

<!-- Hero -->
<section class="bg-[#F9F9F4] py-[80px] lg:py-[120px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px]">
        <p class="font-mono text-[14px] text-[#ee6018] mb-4">Ops & Product Teams</p>
        <h1 class="font-sans text-[48px] lg:text-[64px] tracking-[-0.04em] leading-[1.1] text-[#020202] mb-6">
          Ship AI shaped to your workflows. Skip the six-month procurement cycle.
        </h1>
        <p class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">
          You're the ops manager, the product lead, the COO who knows AI could transform how your team works — if you could actually get it deployed without a year-long internal project or a seven-figure SI contract.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §1: The pitch -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1] mb-8">
          30 minutes to a working instance in your cloud.
        </h2>
        <div class="flex flex-col gap-6">
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            Deploy Corral from Azure Marketplace. In 30 minutes, your team has a working AI foundation in your cloud: assistants they can use immediately, agents you configure for your specific workflows, and an admin console that gives IT the visibility they need to say yes.
          </p>
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            When legal asks about data residency — it runs in your tenant. When security asks about governance — it's built in. When the CFO asks about cost — it's a marketplace subscription, not a capital project.
          </p>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />
</div>

<!-- §2: Day 1 / Day 30 split -->
<section class="bg-[#f9f9f4] py-[80px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="relative">
        <!-- Horizontal lines -->
        <div class="hidden lg:block absolute top-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[63px] top-[-9px]" />
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[63px] top-[-9px]" />

        <div class="hidden lg:block absolute bottom-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[63px] bottom-[-9px]" />
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[63px] bottom-[-9px]" />

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-0">
          <!-- Day 1 -->
          <div class="bg-white border border-[#D3D3D3] p-8 lg:p-10">
            <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider mb-4 block">Day one</span>
            <h3 class="font-sans text-[24px] text-[#020202] tracking-[-0.03em] leading-[1.2] mb-6">
              What you can do immediately.
            </h3>
            <ul class="flex flex-col gap-4">
              {#each dayOne as item}
                <li class="flex items-start gap-3">
                  <span class="w-1.5 h-1.5 bg-[#ee6018] rounded-full mt-2 shrink-0"></span>
                  <span class="font-mono font-light text-[14px] text-[#5C5855] leading-relaxed">{item}</span>
                </li>
              {/each}
            </ul>
          </div>

          <!-- Day 30 -->
          <div class="bg-white border border-[#D3D3D3] p-8 lg:p-10 -mt-px lg:mt-0 lg:-ml-px">
            <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider mb-4 block">Day thirty</span>
            <h3 class="font-sans text-[24px] text-[#020202] tracking-[-0.03em] leading-[1.2] mb-6">
              What you'll have built.
            </h3>
            <ul class="flex flex-col gap-4">
              {#each dayThirty as item}
                <li class="flex items-start gap-3">
                  <span class="w-1.5 h-1.5 bg-[#ee6018] rounded-full mt-2 shrink-0"></span>
                  <span class="font-mono font-light text-[14px] text-[#5C5855] leading-relaxed">{item}</span>
                </li>
              {/each}
            </ul>
          </div>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider: → CTA -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- CTA -->
<ContactCtaSection />
